import { Check, GitBranch, LoaderCircle, Minus, X } from "lucide-react";
import type { ReactNode, Ref } from "react";

export type PlanStepStatus = "pending" | "active" | "success" | "failed" | "blocked" | "cancelled";
const labels: Record<PlanStepStatus, string> = {
  pending: "等待执行",
  active: "正在执行",
  success: "执行完成",
  failed: "执行失败",
  blocked: "因前序失败未执行",
  cancelled: "任务取消",
};

export function WorkspacePlanHeading({
  title,
  objective,
  label = "任务执行",
  actions,
}: {
  title: string;
  objective?: string;
  label?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="workspace-rail-heading">
      <div>
        <p>
          <GitBranch size={13} />
          {label}
        </p>
        <h2 aria-label={objective} className="workspace-plan-objective" title={objective}>
          {title}
        </h2>
      </div>
      {actions && <div className="workspace-rail-heading-actions">{actions}</div>}
    </header>
  );
}

/** 仅展示步骤，不接纳计划、不签发动作，也不构造运行时事件。 */
export function TaskExecutionSteps({
  steps,
  statuses,
  focusIndex = -1,
  focusRef,
  onOpenStep,
}: {
  steps: readonly { id: string; description: string; acceptanceCriteria?: string }[];
  statuses: readonly PlanStepStatus[];
  focusIndex?: number;
  focusRef?: Ref<HTMLLIElement>;
  onOpenStep?: (index: number) => void;
}) {
  return (
    <ol aria-label="计划步骤" className="workspace-plan-axis">
      {steps.map((step, index) => {
        const status = statuses[index] ?? "pending";
        return (
          <li
            className={`workspace-plan-step ${status}`}
            key={step.id}
            ref={index === focusIndex ? focusRef : undefined}
          >
            <div className="workspace-plan-marker">
              <PlanStatusNode status={status} />
              {index < steps.length - 1 && <span className="workspace-plan-line" />}
            </div>
            <div className="workspace-plan-copy">
              <small>
                <span>
                  {String(index + 1).padStart(2, "0")} · {labels[status]}
                </span>
              </small>
              {onOpenStep && status === "success" ? (
                <button
                  className="workspace-step-result"
                  onClick={() => onOpenStep(index)}
                  type="button"
                >
                  <strong>{step.description}</strong>
                </button>
              ) : (
                <strong>{step.description}</strong>
              )}
              {step.acceptanceCriteria && <p>验收：{step.acceptanceCriteria}</p>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function PlanStatusNode({ status }: { status: PlanStepStatus }) {
  return (
    <span aria-label={labels[status]} className={`workspace-plan-status ${status}`} role="img">
      {status === "success" && <Check size={12} strokeWidth={3} />}
      {status === "failed" && <X size={11} strokeWidth={3} />}
      {(status === "blocked" || status === "cancelled") && <Minus size={11} strokeWidth={3} />}
      {status === "active" && (
        <LoaderCircle
          aria-hidden="true"
          className="workspace-plan-status-spinner"
          size={12}
          strokeWidth={2.5}
        />
      )}
    </span>
  );
}
